/**
 * Admin-only study-plan calls.
 *
 * Only the two the console needs beyond the shared authoring seam
 * (`classSharedApi`, which already carries the plan CRUD): the oversight list, and
 * the visibility change that unlisting is.
 *
 * Unlisting is NOT its own route — it is the same visibility call an author makes,
 * so the repair path and the ordinary path cannot drift apart.
 */
import type { StudyPlan } from '@shared-ui/class-shared';
import type { StudyPlanVisibility } from '@shared-ui/class-shared';
import { fetchWithAdmin } from './fetchHelper';
import { writeJson } from './schoolsApi';

/** A published plan, whole — the console lists these in the same table as the
 *  plans it authors, so it needs the same document and not a browse projection.
 *  `authorName` is the one thing the document does not carry. */
export type ListedPlan = StudyPlan & { authorName?: string };

export const studyPlansApi = {
  /**
   * Every published plan, whoever owns it. `subject` narrows to plans covering
   * one — omit it for the oversight list, which wants ONE row per plan rather
   * than one per subject the plan happens to cover.
   */
  listListed: (subject?: string, curriculumId?: string) => {
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (curriculumId) params.set('curriculumId', curriculumId);
    const qs = params.toString();
    return fetchWithAdmin<{ plans: ListedPlan[] }>(`/system/study-plans/listed${qs ? `?${qs}` : ''}`);
  },
  setVisibility: (planId: string, body: { ownerId: string; visibility: StudyPlanVisibility }) =>
    writeJson(`/study-plans/${planId}/visibility`, body),
};
